/**
 * LA SEULE PORTE VERS LE MOTEUR.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ LE FRONT AFFICHE ET DEMANDE. IL NE DECIDE RIEN.                       ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Droits, validations, publications, plafonds, secrets : tout est tranche par
 * l'API. Reproduire une regle ici la ferait diverger au premier ajustement, et
 * un controle cote navigateur se contourne avec un outil de developpement --
 * il ne protege donc rien, il ne fait qu'informer.
 *
 * ── LE COOKIE VOYAGE SEUL ───────────────────────────────────────────────
 *
 * La session est un cookie `__Host-`, pose par l'API et invisible au
 * JavaScript. On n'a donc aucun jeton a stocker, ni a rafraichir, ni a
 * proteger d'un vol par script -- c'est le point ENTIER de ce choix.
 *
 * `credentials: 'same-origin'` suffit : nginx sert l'interface et l'API sous
 * la MEME origine, ce dont le garde d'origine a besoin.
 *
 * ── LE JETON CSRF ACCOMPAGNE CHAQUE MUTATION ────────────────────────────
 *
 * L'API le rend a la connexion et l'exige sur toute methode mutante des
 * familles humaines. Il est garde EN MEMOIRE, jamais dans le stockage local :
 * un jeton persiste survivrait a la session qu'il protege.
 */

export interface ErreurApi {
  readonly code: string;
  readonly message: string;
  readonly statut: number;
  readonly ecarts?: readonly { code: string; message: string; noeud?: string }[];
  // `revision_obsolete` seul : de COMBIEN on a diverge, pas seulement qu on
  // a diverge. Chaine, comme toute revision (bigint cote base).
  readonly attendue?: string;
  readonly reelle?: string;
}

export class EchecApi extends Error {
  readonly detail: ErreurApi;
  constructor(detail: ErreurApi) {
    super(detail.message);
    this.name = 'EchecApi';
    this.detail = detail;
  }
}

/** En memoire uniquement. Perdu au rechargement, comme il se doit. */
let jetonCsrf: string | undefined;

export function poserJetonCsrf(j: string | undefined): void { jetonCsrf = j; }

const MUTANTES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function appeler<T>(
  methode: string, chemin: string, corps?: unknown,
): Promise<T> {
  const entetes: Record<string, string> = {};
  if (corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (MUTANTES.has(methode) && jetonCsrf !== undefined) {
    entetes['x-anelya-csrf'] = jetonCsrf;
  }
  const r = await fetch('/api/v1' + chemin, {
    method: methode,
    credentials: 'same-origin',
    headers: entetes,
    ...(corps !== undefined ? { body: JSON.stringify(corps) } : {}),
  });
  if (r.status === 204) return undefined as T;
  let charge: unknown;
  try { charge = await r.json(); } catch { charge = undefined; }
  if (!r.ok) {
    // LE CONTRAT D'ERREUR EST STABLE : {erreur: {code, message, ecarts?}}.
    // On le lit tel quel plutot que de deviner d'apres le statut -- le code
    // dit la CAUSE, le statut ne dit que la famille.
    const e = (charge as { erreur?: { code?: string; message?: string;
      ecarts?: { code: string; message: string; noeud?: string }[];
      attendue?: string; reelle?: string } } | undefined)?.erreur;
    throw new EchecApi({
      code: e?.code ?? 'reponse_illisible',
      message: e?.message ?? 'Le serveur a répondu de façon inattendue.',
      statut: r.status,
      ...(e?.ecarts !== undefined ? { ecarts: e.ecarts } : {}),
      ...(typeof e?.attendue === 'string' && typeof e?.reelle === 'string'
        ? { attendue: e.attendue, reelle: e.reelle } : {}),
    });
  }
  return charge as T;
}
